// Sphere shader with Perlin noise displacement for audio-reactive visualization

// Classic Perlin 3D Noise - optimized GLSL implementation
const perlinNoise = `
  vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
  vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
  vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
  vec3 fade(vec3 t) { return t*t*t*(t*(t*6.0-15.0)+10.0); }

  float cnoise(vec3 P) {
    vec3 Pi0 = floor(P);
    vec3 Pi1 = Pi0 + vec3(1.0);
    Pi0 = mod289(Pi0);
    Pi1 = mod289(Pi1);
    vec3 Pf0 = fract(P);
    vec3 Pf1 = Pf0 - vec3(1.0);
    vec4 ix = vec4(Pi0.x, Pi1.x, Pi0.x, Pi1.x);
    vec4 iy = vec4(Pi0.yy, Pi1.yy);
    vec4 iz0 = Pi0.zzzz;
    vec4 iz1 = Pi1.zzzz;

    vec4 ixy = permute(permute(ix) + iy);
    vec4 ixy0 = permute(ixy + iz0);
    vec4 ixy1 = permute(ixy + iz1);

    vec4 gx0 = ixy0 * (1.0 / 7.0);
    vec4 gy0 = fract(floor(gx0) * (1.0 / 7.0)) - 0.5;
    gx0 = fract(gx0);
    vec4 gz0 = vec4(0.5) - abs(gx0) - abs(gy0);
    vec4 sz0 = step(gz0, vec4(0.0));
    gx0 -= sz0 * (step(0.0, gx0) - 0.5);
    gy0 -= sz0 * (step(0.0, gy0) - 0.5);

    vec4 gx1 = ixy1 * (1.0 / 7.0);
    vec4 gy1 = fract(floor(gx1) * (1.0 / 7.0)) - 0.5;
    gx1 = fract(gx1);
    vec4 gz1 = vec4(0.5) - abs(gx1) - abs(gy1);
    vec4 sz1 = step(gz1, vec4(0.0));
    gx1 -= sz1 * (step(0.0, gx1) - 0.5);
    gy1 -= sz1 * (step(0.0, gy1) - 0.5);

    vec3 g000 = vec3(gx0.x,gy0.x,gz0.x);
    vec3 g100 = vec3(gx0.y,gy0.y,gz0.y);
    vec3 g010 = vec3(gx0.z,gy0.z,gz0.z);
    vec3 g110 = vec3(gx0.w,gy0.w,gz0.w);
    vec3 g001 = vec3(gx1.x,gy1.x,gz1.x);
    vec3 g101 = vec3(gx1.y,gy1.y,gz1.y);
    vec3 g011 = vec3(gx1.z,gy1.z,gz1.z);
    vec3 g111 = vec3(gx1.w,gy1.w,gz1.w);

    vec4 norm0 = taylorInvSqrt(vec4(dot(g000, g000), dot(g010, g010), dot(g100, g100), dot(g110, g110)));
    g000 *= norm0.x;
    g010 *= norm0.y;
    g100 *= norm0.z;
    g110 *= norm0.w;
    vec4 norm1 = taylorInvSqrt(vec4(dot(g001, g001), dot(g011, g011), dot(g101, g101), dot(g111, g111)));
    g001 *= norm1.x;
    g011 *= norm1.y;
    g101 *= norm1.z;
    g111 *= norm1.w;

    float n000 = dot(g000, Pf0);
    float n100 = dot(g100, vec3(Pf1.x, Pf0.yz));
    float n010 = dot(g010, vec3(Pf0.x, Pf1.y, Pf0.z));
    float n110 = dot(g110, vec3(Pf1.xy, Pf0.z));
    float n001 = dot(g001, vec3(Pf0.xy, Pf1.z));
    float n101 = dot(g101, vec3(Pf1.x, Pf0.y, Pf1.z));
    float n011 = dot(g011, vec3(Pf0.x, Pf1.yz));
    float n111 = dot(g111, Pf1);

    vec3 fade_xyz = fade(Pf0);
    vec4 n_z = mix(vec4(n000, n100, n010, n110), vec4(n001, n101, n011, n111), fade_xyz.z);
    vec2 n_yz = mix(n_z.xy, n_z.zw, fade_xyz.y);
    float n_xyz = mix(n_yz.x, n_yz.y, fade_xyz.x);
    return 2.2 * n_xyz;
  }
`;

export const vertexShader = `
  uniform float uTime;
  uniform float uAudioLevel;
  uniform float uNoiseScale;
  uniform float uDisplacementScale;

  varying vec3 vNormal;
  varying vec3 vPosition;
  varying float vDisplacement;

  ${perlinNoise}

  void main() {
    vNormal = normalize(normalMatrix * normal);
    vPosition = position;

    // Base noise frequency - creates organic movement
    float noiseFreq = uNoiseScale;

    // Time-based animation speed
    float timeScale = uTime * 0.3;

    // Layer multiple noise octaves for more organic look
    float noise1 = cnoise(position * noiseFreq + timeScale);
    float noise2 = cnoise(position * noiseFreq * 2.0 + timeScale * 1.5) * 0.5;
    float noise3 = cnoise(position * noiseFreq * 4.0 + timeScale * 2.0) * 0.25;

    // Combine noise layers
    float combinedNoise = noise1 + noise2 + noise3;

    // Base displacement (idle animation)
    float baseDisplacement = combinedNoise * 0.1;

    // Audio-reactive displacement - increases with audio level
    float audioDisplacement = combinedNoise * uAudioLevel * uDisplacementScale;

    // Total displacement
    float displacement = baseDisplacement + audioDisplacement;
    vDisplacement = displacement;

    // Displace vertex along normal
    vec3 newPosition = position + normal * displacement;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
  }
`;

export const fragmentShader = `
  uniform vec3 uColor;
  uniform vec3 uEmissiveColor;
  uniform float uTime;
  uniform float uAudioLevel;
  uniform float uProgress;  // 0.0 - 1.0 for progress ring

  varying vec3 vNormal;
  varying vec3 vPosition;
  varying float vDisplacement;

  void main() {
    // Fresnel effect for edge glow
    vec3 viewDirection = normalize(cameraPosition - vPosition);
    float fresnel = pow(1.0 - dot(viewDirection, vNormal), 2.0);

    // Base color with displacement influence
    vec3 baseColor = uColor;

    // Add highlight based on displacement
    float highlight = smoothstep(0.0, 0.3, vDisplacement) * 0.3;

    // Emissive glow that increases with audio
    vec3 emissive = uEmissiveColor * (0.2 + uAudioLevel * 0.5);

    // Combine colors
    vec3 finalColor = baseColor + highlight + emissive * fresnel;

    // Progress ring effect - fills around sphere equator
    if (uProgress > 0.0) {
      // Calculate angle around Y axis (horizontal ring)
      float angle = atan(vPosition.z, vPosition.x);  // -PI to PI
      float normalizedAngle = (angle + 3.14159265) / (2.0 * 3.14159265);  // 0 to 1

      // Create smooth progress ring with glow
      float ringWidth = 0.15;  // Width of the progress band
      float equatorBand = 1.0 - abs(vNormal.y);  // Strongest at equator
      equatorBand = smoothstep(0.3, 1.0, equatorBand);

      // Progress fill with soft edge
      float progressFill = smoothstep(uProgress - 0.02, uProgress, normalizedAngle);
      float progressRing = (1.0 - progressFill) * equatorBand * 0.4;

      // Add progress glow to final color
      finalColor += uEmissiveColor * progressRing * (1.0 + sin(uTime * 3.0) * 0.2);
    }

    // Add subtle pulsing glow
    float pulse = sin(uTime * 2.0) * 0.5 + 0.5;
    finalColor += uEmissiveColor * pulse * 0.1 * (1.0 + uAudioLevel);

    // Rim lighting
    finalColor += fresnel * uEmissiveColor * 0.5;

    gl_FragColor = vec4(finalColor, 1.0);
  }
`;

// Color presets for different states (matching Tailwind theme)
export const stateColors = {
  idle: {
    color: [0.231, 0.510, 0.965],      // #3b82f6 - blue-500
    emissive: [0.149, 0.306, 0.847],   // #2563eb - blue-600
  },
  recording: {
    color: [0.133, 0.773, 0.369],      // #22c55e - green-500
    emissive: [0.086, 0.639, 0.290],   // #16a34a - green-600
  },
  paused: {
    color: [0.961, 0.620, 0.043],      // #f59e0b - amber-500
    emissive: [0.851, 0.467, 0.024],   // #d97706 - amber-600
  },
  encrypting: {
    color: [0.388, 0.400, 0.945],      // #6366f1 - indigo-500
    emissive: [0.310, 0.318, 0.878],   // #4f46e5 - indigo-600
  },
  uploading: {
    color: [0.388, 0.400, 0.945],      // #6366f1 - indigo-500
    emissive: [0.310, 0.318, 0.878],   // #4f46e5 - indigo-600
  },
  complete: {
    color: [0.133, 0.773, 0.369],      // #22c55e - green-500
    emissive: [0.086, 0.639, 0.290],   // #16a34a - green-600
  },
} as const;

export type SphereState = keyof typeof stateColors;

// Color presets for progress stages (transcription pipeline)
export const progressColors: Record<string, { color: number[]; emissive: number[] }> = {
  securing: {
    color: [0.388, 0.400, 0.945],      // #6366f1 - indigo-500
    emissive: [0.310, 0.275, 0.898],   // Indigo glow
  },
  decrypting: {
    color: [0.545, 0.361, 0.965],      // #8b5cf6 - violet-500
    emissive: [0.486, 0.227, 0.929],   // Violet glow
  },
  converting: {
    color: [0.678, 0.286, 0.906],      // #a855f7 - purple-500
    emissive: [0.576, 0.200, 0.859],   // Purple glow
  },
  transcribing: {
    color: [0.059, 0.647, 0.914],      // #06b6d4 - cyan-500
    emissive: [0.027, 0.506, 0.792],   // Cyan glow
  },
  enhancing: {
    color: [0.078, 0.722, 0.651],      // #14b8a6 - teal-500
    emissive: [0.039, 0.569, 0.510],   // Teal glow
  },
  finalizing: {
    color: [0.133, 0.773, 0.369],      // #22c55e - green-500
    emissive: [0.086, 0.643, 0.290],   // Green glow
  },
  complete: {
    color: [0.133, 0.773, 0.369],      // #22c55e - green-500
    emissive: [0.086, 0.643, 0.290],   // Green glow
  },
};

// CSS glow colors for progress stages
export const progressGlowColors: Record<string, string> = {
  securing: 'rgba(99, 102, 241, 0.4)',     // Indigo
  decrypting: 'rgba(139, 92, 246, 0.4)',   // Violet
  converting: 'rgba(168, 85, 247, 0.4)',   // Purple
  transcribing: 'rgba(6, 182, 212, 0.4)',  // Cyan
  enhancing: 'rgba(20, 184, 166, 0.4)',    // Teal
  finalizing: 'rgba(34, 197, 94, 0.4)',    // Green
  complete: 'rgba(34, 197, 94, 0.4)',      // Green
};
